import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { DueDateAttachment, RenameDueDateAttachment, ShowDueDateAttachmentToUser } from "./types";

export const useMyCarDueDateAttachmentApi = () => {
  const api = useApi();

  const useDueDateAttachments = useCallback(
    (query: PaginationQuery, signal: AbortSignal, dueDateId: string, isTeam: boolean) => {
      const uri = isTeam ? "my_team_cars" : "my_cars";
      const url = buildUrl(`/${uri}/due_dates/${dueDateId}/attachments`, query);
      return useSWR<Paginated<DueDateAttachment>, CustomError, string>(url, (url) =>
        api.get<Paginated<DueDateAttachment>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    useDueDateAttachments,
  };
};

export const useDueDateAttachmentApi = () => {
  const api = useApi();

  const useAdminDueDateAttachments = useCallback(
    (query: PaginationQuery, signal: AbortSignal, dueDateId: string) => {
      const url = buildUrl(`/admin/due_dates/${dueDateId}/attachments`, query);
      return useSWR<Paginated<DueDateAttachment>, CustomError, string>(url, (url) =>
        api.get<Paginated<DueDateAttachment>>({ url, signal }),
      );
    },
    [api],
  );

  const deleteDueDateAttachment = useCallback(
    async (documentId: string, dueDateId: string): Promise<void> => {
      const url = `/admin/due_date_attachments/${documentId}`;
      await api.del(url);
      api.mutateCache([`/admin/due_dates/${dueDateId}/attachments`]);
    },
    [api],
  );

  const insertDueDateAttachment = useCallback(
    async (file: File, overWrite?: boolean, dueDateId?: string): Promise<void> => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      const url = `/admin/due_dates/${dueDateId}/attachments${overWrite ? "?overwrite=1" : ""}`;
      await api.postNoResult<FormData>(url, formData);
      api.mutateCache([`/admin/due_dates/${dueDateId}/attachments`]);
    },
    [api],
  );

  const renameDueDateAttachment = useCallback(
    async (docId: string, body: RenameDueDateAttachment, dueDateId?: string): Promise<void> => {
      await api.put<RenameDueDateAttachment>(`/admin/due_date_attachments/${docId}`, body);
      api.mutateCache([`/admin/due_dates/${dueDateId}/attachments`]);
    },
    [api],
  );

  const updateDueDateAttachmentVisibleToUser = useCallback(
    async (docId: string, body: ShowDueDateAttachmentToUser, dueDateId: string, carId: string): Promise<void> => {
      await api.put<ShowDueDateAttachmentToUser>(`/admin/due_date_attachments/${docId}/show_to_user`, body);
      api.mutateCache([
        `/admin/due_dates/${dueDateId}/attachments`,
        `/my_cars/due_dates/${dueDateId}/attachments`,
        `/my_team_cars/due_dates/${dueDateId}/attachments`,
        `/my_cars/${carId}/due_dates`,
        `/my_team_cars/${carId}/due_dates`,
      ]);
    },
    [api],
  );

  return {
    useAdminDueDateAttachments,
    deleteDueDateAttachment,
    insertDueDateAttachment,
    renameDueDateAttachment,
    updateDueDateAttachmentVisibleToUser,
  };
};
